// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/logger/types.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * Logger Service Interface
 * 
 * Defines the contract for logging services used throughout the application.
 * This abstraction allows for different implementations (console, file, remote, etc.)
 * and makes testing easier by allowing mock loggers.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  category?: string;
  data?: unknown;
  stack?: string;
}

export interface LoggerOptions {
  level?: LogLevel;
  enableColors?: boolean;
  enableTimestamps?: boolean;
  includeStack?: boolean;
  category?: string;
}

export interface Logger {
  /**
   * Log a debug message
   */
  debug(message: string, data?: unknown): void;

  /**
   * Log an informational message
   */
  info(message: string, data?: unknown): void;

  /**
   * Log a warning message
   */
  warn(message: string, data?: unknown): void;

  /**
   * Log an error message
   */
  error(message: string, error?: Error | unknown): void;

  /**
   * Set the minimum log level
   */
  setLevel(level: LogLevel): void;

  /**
   * Get the current log level
   */
  getLevel(): LogLevel;

  /**
   * Create a new logger with a specific category
   */
  withCategory(category: string): Logger;
}